import { Check, ShieldCheck, Users, Wallet } from 'lucide-react'
import { Helmet } from 'react-helmet'

import dividendPaybackIcon from '@/assets/marketing/images/group-captive-icon-dividend-payback.png'
import fastEnrollIcon from '@/assets/marketing/images/group-captive-icon-fast-enroll.png'
import fleetShieldIcon from '@/assets/marketing/images/group-captive-icon-fleet-shield.png'
import lossTransparencyIcon from '@/assets/marketing/images/group-captive-icon-loss-transparency.png'
import { Anchor } from '@/components/ui/Anchor'
import { Button } from '@/components/ui/button'
import { trackMarketingCta } from '@/lib/analytics'
import { Checklist } from './components/Checklist'
import { DataCard, type DataRow } from './components/DataCard'
import { HubSpotFormEmbed } from './components/HubSpotFormEmbed'
import { Reveal } from './components/Reveal'
import { ValueProp } from './components/ValueProp'

const HUBSPOT_PORTAL_ID = '246335544'
const HUBSPOT_FORM_ID = '9efa6ca5-f329-416d-a678-c83ceff5790b'
const HUBSPOT_REGION = 'na2'

const VALUE_PROPS = [
  {
    key: 'member-owned',
    icon: <Users />,
    title: 'Member-owned',
    body: 'Profits returned to you, not paid to an insurance company',
  },
  {
    key: 'a-rated',
    icon: <ShieldCheck />,
    title: 'A-rated policies',
    body: 'Backed by an A-rated insurer, trusted by shippers and brokers',
  },
  {
    key: 'financed-collateral',
    icon: <Wallet />,
    title: 'Financed collateral',
    body: 'Simple and affordable collateral financing, lined up by Cai',
  },
]

const HOW_IT_WORKS = [
  {
    number: '01',
    image: fastEnrollIcon,
    title: 'Enroll in days, not months',
    body: 'Fast underwriting decisions and policy updates.',
  },
  {
    number: '02',
    image: fleetShieldIcon,
    title: 'Join forces with other qualified fleets',
    body: "Each member's premium funds its own losses first, and you're only pooled with operators who passed the same high bar.",
  },
  {
    number: '03',
    image: lossTransparencyIcon,
    title: 'See where every dollar goes',
    body: 'You see the loss funds, the fees, and the returns. No black box.',
  },
  {
    number: '04',
    image: dividendPaybackIcon,
    title: 'Safe years pay you back',
    body: 'Underwriting profit accumulates for members, paid out as dividends over time.',
  },
]

const MEMBERSHIP_CRITERIA = [
  '20+ trucks in operation',
  '3+ years of favorable loss history',
  '$300K+ in annual insurance premiums',
  'Cameras or telematics (or a plan to add them)',
]

const TRUST_ROWS: DataRow[] = [
  { label: 'Safety reviews', value: 'Included', status: 'ok', icon: <Check /> },
  { label: 'Claims advocacy', value: 'Included', status: 'ok', icon: <Check /> },
  { label: 'Loss control', value: 'Included', status: 'ok', icon: <Check /> },
  { label: 'Instant COIs, any time', value: 'Included', status: 'ok', icon: <Check /> },
  { label: 'Policy changes in clicks, not calls', value: 'Included', status: 'ok', icon: <Check /> },
]

export function GroupCaptive() {
  return (
    <>
      <Helmet>
        <title>Group Captive Insurance for Trucking</title>
      </Helmet>
      <meta
        name="description"
        content="A member-owned insurance program offering Auto Liability and Physical Damage coverage for safety-first trucking companies. Run safe, and the profits come back to members."
      />

      <section
        id="apply"
        className="mx-auto grid max-w-(--maxw) grid-cols-2 items-start gap-14 px-5.5 py-24 max-lg:grid-cols-1"
      >
        <Reveal className="duration-[900ms]" delay={400}>
          <span className="eyebrow hero-eyebrow">Group captive for trucking</span>
          <h1 className="h1-hero mt-5 text-pretty">
            Good fleets shouldn&apos;t pay for everyone else&apos;s wrecks.
          </h1>
          <p className="text-ink-500 mt-7 max-w-140 text-xl leading-[1.6] text-pretty">
            A member-owned insurance program offering Auto Liability and Physical Damage coverage for
            safety-first trucking companies. Run safe, and the profits come back to members.
          </p>
        </Reveal>
        <Reveal className="border-ink-100 rounded-sm border bg-white p-7 duration-[900ms]" delay={800}>
          <span className="block px-10 text-2xl font-bold tracking-[-0.01em] text-balance">
            See if your fleet qualifies for the Cai Group Captive
          </span>
          <HubSpotFormEmbed
            portalId={HUBSPOT_PORTAL_ID}
            formId={HUBSPOT_FORM_ID}
            region={HUBSPOT_REGION}
            onSubmitted={() => trackMarketingCta('group_captive_apply')}
            className="mt-1"
          />
          <p className="text-ink-400 mt-5 px-10 font-mono text-[10px] leading-[1.7]">
            By submitting, you agree Cai may contact you about the Group Captive program. See our{' '}
            <Anchor href="/privacy">Privacy Policy</Anchor>.
          </p>
        </Reveal>
      </section>

      <section className="border-ink-100 border-t bg-white px-5.5 py-16">
        <div className="mx-auto grid max-w-(--maxw) grid-cols-3 gap-10 max-md:grid-cols-1">
          {VALUE_PROPS.map((prop) => (
            <Reveal key={prop.key}>
              <ValueProp icon={prop.icon} title={prop.title}>
                {prop.body}
              </ValueProp>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="how" className="bg-brand-100 px-5.5 py-24 lg:py-32">
        <div className="mx-auto max-w-(--maxw)">
          <Reveal>
            <span className="eyebrow">How it works</span>
          </Reveal>
          <div className="mt-11 grid grid-cols-4 gap-10 max-lg:grid-cols-2 max-md:grid-cols-1">
            {HOW_IT_WORKS.map((step, i) => (
              <Reveal key={step.number} delay={i * 100}>
                <div className="flex items-center gap-2">
                  <div className="bg-brand-500 size-2.75 flex-none rounded-full" />
                  <div className="bg-ink-200 h-px flex-1" />
                </div>
                <div className="text-brand-800 mt-5 font-mono text-xs tracking-[0.08em]">{step.number}</div>
                <div className="border-ink-200 mt-4 aspect-3/2 rounded-sm border bg-white p-10">
                  <img src={step.image} alt="" className="h-full w-full object-contain" />
                </div>
                <div className="mt-4.5 text-2xl font-bold tracking-[-0.01em]">{step.title}</div>
                <p className="text-md text-ink-500 mt-2.5 leading-[1.55]">{step.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-(--maxw) grid-cols-2 items-start gap-12 px-5.5 py-24 max-md:grid-cols-1">
        <Reveal>
          <span className="eyebrow">Membership criteria</span>
          <h2 className="h3 mt-4.5">Who is eligible</h2>
          <p className="text-md text-ink-500 mt-4.5 max-w-100 leading-[1.55]">
            Every fleet is reviewed individually. Members have the opportunity to approve or decline new
            applicants. We&apos;ll give you an answer in days, not months.
          </p>
        </Reveal>
        <Reveal>
          <Checklist items={MEMBERSHIP_CRITERIA} />
        </Reveal>
      </section>

      <section className="bg-brand-900 px-5.5 py-24 lg:py-32">
        <Reveal className="mx-auto max-w-225 text-center text-white">
          <span className="text-brand-500 font-mono text-xs tracking-[0.14em] uppercase">
            Cai&apos;s role
          </span>
          <h2 className="h2 mt-4.5 text-white">Support managing your risk and your policy</h2>
          <p className="text-md mx-auto mt-4.5 max-w-120 leading-[1.55] text-white/70">
            Cai is the insurance broker and risk manager for the program, and every member gets access to
            Cai&apos;s AI-powered platform.
          </p>
          <DataCard rows={TRUST_ROWS} className="mt-10 text-left" />
        </Reveal>
      </section>

      <section className="border-ink-100 bg-ink-50 border-t px-5.5 py-20">
        <Reveal className="mx-auto flex max-w-(--maxw) flex-wrap items-center justify-between gap-7">
          <div>
            <h2 className="h3">See if your fleet qualifies</h2>
            <p className="text-md text-ink-500 mt-3 max-w-140 leading-[1.55]">
              Apply today to start investing in your fleet instead of paying an insurance company.
            </p>
          </div>
          <Button size="lg" href="#apply">
            Request a review
          </Button>
        </Reveal>
      </section>
    </>
  )
}
